import {
  type AiManagedGroupCreateInput,
  type AiManagedGroupEligibleCampaign,
  type AiManagedGroupPolicy,
  MAX_MANAGED_GROUP_MEMBERS,
  ManagedGroupAsinFilterSchema,
  resolveExecutionModeV1,
} from "@adpilot/shared";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { marketplaceCode } from "@/components/layout/shop-scope-select";
import type {
  CandidateAiFilter,
  CandidateDeliveryFilter,
  CandidatePortfolioFilter,
} from "@/components/managed-group/ManagedGroupCandidatePicker";
import { fetchAmazonAccounts } from "@/lib/amazon-api";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  createManagedGroup,
  fetchEligibleManagedGroupCampaigns,
  fetchManagedGroupDiagnostics,
  fetchManagedGroupPortfolioOptions,
  selectAllEligibleManagedGroupCampaigns,
} from "@/lib/managed-group-api";
import { fetchSystemAiPolicyForShop } from "@/lib/settings-api";
import { useAuthStore } from "@/stores/auth";
import { CAMPAIGN_PAGE_SIZE } from "./campaign-selection";
import {
  canReuseManagedGroupDiagnosticsPlaceholder,
  managedGroupDiagnosticsQueryKey,
} from "./managed-group-diagnostics-query";

interface UseCreateManagedGroupWizardOptions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Wizard state, server queries, and the create mutation behind `CreateManagedGroupDialog`. */
export function useCreateManagedGroupWizard({
  open,
  onOpenChange,
}: UseCreateManagedGroupWizardOptions) {
  const { t } = useTranslation("managedGroups");
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const shops = useAuthStore((state) => state.shops);
  const currentShopId = useAuthStore((state) => state.currentShopId);
  const workspaceShops = shops.filter((shop) => shop.workspaceId === user?.activeWorkspaceId);
  const accountsQuery = useQuery({
    queryKey: ["amazon", "accounts"],
    queryFn: fetchAmazonAccounts,
    staleTime: 60_000,
    enabled: open,
  });
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [shopId, setShopId] = useState(currentShopId ?? workspaceShops[0]?.shopId ?? "");
  const [name, setName] = useState("");
  const [selectedCampaigns, setSelectedCampaigns] = useState<
    Map<string, AiManagedGroupEligibleCampaign>
  >(new Map());
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignAsins, setCampaignAsins] = useState("");
  const [campaignDeliveryStatus, setCampaignDeliveryStatus] =
    useState<CandidateDeliveryFilter>("enabled");
  const [campaignPortfolioId, setCampaignPortfolioId] = useState<CandidatePortfolioFilter>("all");
  const [campaignAiStatus, setCampaignAiStatus] = useState<CandidateAiFilter>("all");
  const [selectedCampaignPage, setSelectedCampaignPage] = useState(1);
  const [policy, setPolicy] = useState<AiManagedGroupPolicy>({
    businessGoal: "improve_efficiency",
    targetAcos: 0.3,
    riskPreference: "balanced",
    executionMode: "recommendation",
  });

  const excludedCampaignIds = [...selectedCampaigns.keys()].sort();
  const parsedAsins = ManagedGroupAsinFilterSchema.safeParse([
    ...new Set(
      campaignAsins
        .toUpperCase()
        .split(/[\s,，;；]+/)
        .filter(Boolean),
    ),
  ]);
  const asinError = !parsedAsins.success;
  const candidateFilters = {
    search: campaignSearch.trim(),
    asins: parsedAsins.success ? parsedAsins.data : [],
    status: campaignDeliveryStatus === "all" ? undefined : campaignDeliveryStatus,
    portfolioId: campaignPortfolioId === "all" ? undefined : campaignPortfolioId,
    aiStatus: campaignAiStatus === "all" ? undefined : campaignAiStatus,
    sortBy: "delivery" as const,
    sortDirection: "asc" as const,
    excludeCampaignIds: excludedCampaignIds,
  };
  const campaignsQuery = useQuery({
    queryKey: [
      "ai-managed-groups",
      "eligible-campaigns",
      shopId,
      campaignPage,
      campaignSearch,
      campaignAsins,
      campaignDeliveryStatus,
      campaignPortfolioId,
      campaignAiStatus,
      excludedCampaignIds,
    ],
    queryFn: () =>
      fetchEligibleManagedGroupCampaigns(shopId, {
        page: campaignPage,
        pageSize: CAMPAIGN_PAGE_SIZE,
        ...candidateFilters,
      }),
    enabled: open && Boolean(shopId) && !asinError,
  });
  const selectAllCampaigns = (signal: AbortSignal) =>
    selectAllEligibleManagedGroupCampaigns(shopId, candidateFilters, signal);
  const portfoliosQuery = useQuery({
    queryKey: ["ai-managed-groups", "portfolio-options", shopId],
    queryFn: () => fetchManagedGroupPortfolioOptions(shopId),
    enabled: open && Boolean(shopId),
  });
  const systemQuery = useQuery({
    queryKey: ["settings", "ai-policy", shopId],
    queryFn: () => fetchSystemAiPolicyForShop(shopId),
    enabled: open && Boolean(shopId),
  });
  const diagnosticsLifecycleStatus = systemQuery.data?.systemEnabled ? "active" : "paused";
  const diagnosticsQueryKey = managedGroupDiagnosticsQueryKey(
    shopId,
    [...selectedCampaigns.keys()].sort(),
    policy,
    diagnosticsLifecycleStatus,
  );
  const diagnosticsQuery = useQuery({
    queryKey: diagnosticsQueryKey,
    queryFn: () =>
      fetchManagedGroupDiagnostics(shopId, {
        schemaVersion: 1,
        campaignIds: [...selectedCampaigns.keys()],
        policy,
        lifecycleStatus: diagnosticsLifecycleStatus,
      }),
    enabled:
      open &&
      step >= 2 &&
      Boolean(shopId) &&
      selectedCampaigns.size > 0 &&
      systemQuery.data !== undefined,
    // Policy-only edits may reuse the current scope's result; Shop, Campaign, and lifecycle
    // changes must wait for a fresh safety classification before Continue can be enabled.
    placeholderData: (previousData, previousQuery) =>
      canReuseManagedGroupDiagnosticsPlaceholder(previousQuery?.queryKey, diagnosticsQueryKey)
        ? keepPreviousData(previousData)
        : undefined,
  });

  useEffect(() => {
    const system = systemQuery.data;
    if (!system) return;
    setPolicy({
      businessGoal: system.policy.businessGoal,
      targetAcos: system.policy.targetAcos,
      riskPreference: system.policy.riskPreference,
      executionMode: resolveExecutionModeV1(system.policy),
    });
  }, [systemQuery.data]);

  const mutation = useMutation({
    mutationFn: (lifecycleStatus: "active" | "paused") => {
      const input: AiManagedGroupCreateInput = {
        name: name.trim(),
        campaignIds: [...selectedCampaigns.keys()],
        policy,
        lifecycleStatus,
      };
      return createManagedGroup(shopId, input);
    },
    onSuccess: async (group) => {
      toast.success(t("create.success", { name: group.name }));
      await queryClient.invalidateQueries({ queryKey: ["ai-managed-groups"] });
      reset();
      onOpenChange(false);
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });

  function reset(): void {
    setStep(1);
    setName("");
    setSelectedCampaigns(new Map());
    setCampaignPage(1);
    setCampaignSearch("");
    setCampaignAsins("");
    setCampaignDeliveryStatus("enabled");
    setCampaignPortfolioId("all");
    setCampaignAiStatus("all");
    setSelectedCampaignPage(1);
  }

  function changeShop(nextShopId: string | null): void {
    if (!nextShopId) return;
    setShopId(nextShopId);
    setSelectedCampaigns(new Map());
    setCampaignPage(1);
    setCampaignSearch("");
    setCampaignAsins("");
    setCampaignDeliveryStatus("enabled");
    setCampaignPortfolioId("all");
    setCampaignAiStatus("all");
    setSelectedCampaignPage(1);
  }

  function addCampaigns(campaignsToAdd: AiManagedGroupEligibleCampaign[]): void {
    const combined = new Set([
      ...selectedCampaigns.keys(),
      ...campaignsToAdd.map((campaign) => campaign.id),
    ]);
    if (combined.size > MAX_MANAGED_GROUP_MEMBERS) {
      toast.error(t("create.selectionLimit", { limit: MAX_MANAGED_GROUP_MEMBERS }));
      return;
    }
    setSelectedCampaigns((current) => {
      const next = new Map(current);
      for (const campaign of campaignsToAdd) {
        next.set(campaign.id, campaign);
      }
      return next;
    });
    setCampaignPage(1);
    setSelectedCampaignPage(1);
  }

  function removeCampaigns(campaignIds: string[]): void {
    setSelectedCampaigns((current) => {
      const next = new Map(current);
      for (const campaignId of campaignIds) next.delete(campaignId);
      return next;
    });
    setCampaignPage(1);
    setSelectedCampaignPage(1);
  }

  // Every candidate filter change restarts the available list at its first page.
  function changeCampaignSearch(value: string): void {
    setCampaignSearch(value);
    setCampaignPage(1);
  }

  function changeCampaignAsins(value: string): void {
    setCampaignAsins(value);
    setCampaignPage(1);
  }

  function changeCampaignDeliveryStatus(value: CandidateDeliveryFilter): void {
    setCampaignDeliveryStatus(value);
    setCampaignPage(1);
  }

  function changeCampaignPortfolioId(value: CandidatePortfolioFilter): void {
    setCampaignPortfolioId(value);
    setCampaignPage(1);
  }

  function changeCampaignAiStatus(value: CandidateAiFilter): void {
    setCampaignAiStatus(value);
    setCampaignPage(1);
  }

  const selectedCount = selectedCampaigns.size;
  const selectedValues = [...selectedCampaigns.values()];
  const alreadyAiOnCount = selectedValues.filter((campaign) => campaign.aiManaged).length;
  const canContinueScope = name.trim().length > 0 && selectedCount > 0;
  const canContinuePolicy =
    policy.targetAcos >= 0.01 &&
    policy.targetAcos <= 2 &&
    Boolean(diagnosticsQuery.data) &&
    diagnosticsQuery.data?.classification !== "blocked" &&
    !diagnosticsQuery.isError;
  const workspaceAiEnabled = systemQuery.data?.systemEnabled ?? false;
  const selectedShop = workspaceShops.find((shop) => shop.shopId === shopId);
  const accountShops = accountsQuery.data?.connections.flatMap((connection) => connection.shops);
  const shopItems = workspaceShops.map((shop) => {
    const account = accountShops?.find((accountShop) => accountShop.shopId === shop.shopId);
    return {
      value: shop.shopId,
      label: shop.shopName,
      country: account?.marketplaceName ? marketplaceCode(account.marketplaceName) : undefined,
    };
  });
  const campaignPageData = campaignsQuery.data;
  const campaigns = campaignPageData?.data ?? [];
  const campaignTotal = campaignPageData?.pagination.total ?? 0;
  const campaignPageSize = campaignPageData?.pagination.pageSize ?? CAMPAIGN_PAGE_SIZE;

  return {
    step,
    setStep,
    shopId,
    name,
    setName,
    selectedCampaigns,
    campaignPage,
    setCampaignPage,
    campaignSearch,
    campaignAsins,
    asinError,
    selectAllCampaigns,
    changeCampaignAsins,
    campaignDeliveryStatus,
    campaignPortfolioId,
    campaignAiStatus,
    selectedCampaignPage,
    setSelectedCampaignPage,
    policy,
    setPolicy,
    campaignsQuery,
    portfoliosQuery,
    diagnosticsQuery,
    mutation,
    reset,
    changeShop,
    addCampaigns,
    removeCampaigns,
    changeCampaignSearch,
    changeCampaignDeliveryStatus,
    changeCampaignPortfolioId,
    changeCampaignAiStatus,
    selectedCount,
    alreadyAiOnCount,
    canContinueScope,
    canContinuePolicy,
    workspaceAiEnabled,
    selectedShop,
    shopItems,
    campaigns,
    campaignTotal,
    campaignPageSize,
  };
}
